/**
 * MarkerRow — the pins above the scrubber. A human note is a purple dot and
 * an AI flag is a red tick: deliberately different shapes, so the two read
 * apart without relying on colour alone.
 *
 * Hovering a note shows a thumbnail (its captured frame, or the video at that
 * time) through the rail's own scrub preview; an AI flag keeps a text tooltip.
 */
import { useCallback, useRef } from 'react';

import { cn } from '../../../../../lib/utils';
import type { Marker } from '../lib/buildMarkers';
import { formatTimecode } from '../../../../../lib/media/timecode';
import type { NotesSession } from '../session/useNotesSession';
import { aiTooltipOf, isAiMarker, type MarkerHover } from './markerPreview';
import { clampToRail, isPortrait, noteEdgeInsetPx } from './previewFrame';

const FULL_PCT = 100;
/** The tooltip's max width; half of it keeps the box on-screen. */
const TOOLTIP_MAX_WIDTH_PX = 238;
const TOOLTIP_EDGE_INSET_PX = TOOLTIP_MAX_WIDTH_PX / 2;

/**
 * The button is 14x11 to match the pins; this grows its hit area to 24x24
 * (WCAG 2.5.8) without changing what is drawn. A hovered or focused marker
 * rises above its neighbours, so overlapping ones each stay reachable.
 */
const MARKER_BUTTON_CLASSES =
  "group/mark absolute top-0 flex h-[11px] w-[14px] -translate-x-[7px] cursor-pointer items-center justify-center hover:z-10 focus-visible:z-10 before:absolute before:top-1/2 before:left-1/2 before:size-6 before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']";

const PIN_BASE_CLASSES = 'block transition-all duration-150 motion-reduce:transition-none';
const AI_TICK_CLASSES =
  'h-[8px] w-[2px] rounded-[2px] bg-red-400 group-hover/mark:h-[11px] group-hover/mark:w-[3px] group-hover/mark:bg-red-500';
/** The ring is the player's own background, cutting the dot out of whatever it overlaps. */
const NOTE_DOT_CLASSES =
  'size-[8px] rounded-full bg-violet-500 ring-[1.5px] ring-(--background-player)/90 group-hover/mark:size-[10px] group-hover/mark:bg-violet-400 group-hover/mark:ring-2';

export interface MarkerRowProps {
  session: NotesSession;
  hovered: MarkerHover | null;
  onHoverChange: (hover: MarkerHover | null) => void;
}

function pinClasses(marker: Marker): string {
  if (isAiMarker(marker)) return AI_TICK_CLASSES;
  return NOTE_DOT_CLASSES;
}

function markerLabel(marker: Marker): string {
  const at = formatTimecode(marker.tMs);
  // The flag's text is otherwise only in the hover tooltip, which a screen reader never reaches.
  if (isAiMarker(marker)) return `${marker.author} at ${at}: ${marker.snippet}`;
  return `Note by ${marker.author} at ${at}`;
}

/** An AI flag's tooltip is wide text; a note's preview is its thumbnail plus its text box. */
function edgeInsetFor(marker: Marker, portrait: boolean): number {
  if (isAiMarker(marker)) return TOOLTIP_EDGE_INSET_PX;
  return noteEdgeInsetPx(portrait);
}

export function MarkerRow({ session, hovered, onHoverChange }: MarkerRowProps) {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const portrait = isPortrait(session.controller.intrinsic);

  const showHover = useCallback(
    (marker: Marker) => {
      const width = rowRef.current?.clientWidth ?? 0;
      const raw = (marker.leftPct / FULL_PCT) * width;
      onHoverChange({ marker, leftPx: clampToRail(raw, width, edgeInsetFor(marker, portrait)) });
    },
    [onHoverChange, portrait],
  );

  const hideHover = useCallback(() => onHoverChange(null), [onHoverChange]);

  const seekToMarker = useCallback(
    (marker: Marker) => {
      // The session seeks, and for a chat moment also scrolls the chat to it.
      session.selectMarker(marker);
      hideHover();
    },
    [session, hideHover],
  );

  const tooltip = aiTooltipOf(hovered);

  return (
    <div ref={rowRef} className="relative h-[11px]" data-testid="marker-row">
      {session.markers.map((marker) => (
        <button
          key={marker.id}
          type="button"
          aria-label={markerLabel(marker)}
          className={MARKER_BUTTON_CLASSES}
          style={{ left: `${marker.leftPct}%` }}
          onMouseEnter={() => showHover(marker)}
          onMouseLeave={hideHover}
          onFocus={() => showHover(marker)}
          onBlur={hideHover}
          onClick={() => seekToMarker(marker)}
        >
          <i aria-hidden="true" className={cn(PIN_BASE_CLASSES, pinClasses(marker))} />
        </button>
      ))}

      {tooltip && (
        // A React node, never innerHTML — the flag's label is model text.
        <div
          role="tooltip"
          className="pointer-events-none absolute bottom-[15px] z-[60] w-max -translate-x-1/2 rounded-[9px] bg-zinc-900 px-2.5 py-[7px] text-[11.5px] leading-[1.4] text-white"
          style={{ left: `${tooltip.leftPx}px`, maxWidth: TOOLTIP_MAX_WIDTH_PX }}
        >
          <b className="font-semibold text-white">{tooltip.marker.title}</b>
          <br />
          {tooltip.marker.snippet}
        </div>
      )}
    </div>
  );
}
